import { useState } from "react";
import SbuxStyledTableRow from "../../../../../components/SbuxTable/SbuxStyledTableRow";
import SbuxStyledTableCell from "../../../../../components/SbuxTable/SbuxStyledTableCell";
import SbuxDialog2 from "../../../../../components/SbuxDialog2";

import { useTheme } from "@mui/material/styles";
import SbuxAutocomplete from "../../../../../components/SbuxAutoComplete";
import {
  getDays,
  getTime,
  type TimeOption,
} from "../../../../../utils/timeUtil";
import type { HoursConfigRow } from "./types";
import {
  Checkbox,
  Box,
  Button,
  DialogContent,
  DialogActions,
  Typography,
} from "@mui/material";
import dayjs from "../../../../../utils/dayjs";
import { StyledDeleteButton } from "../../styled";
import SbuxTranslate from "../../../../../components/SbuxTranslate";

interface DayOption {
  value: string;
  label: string;
}

type DataRecord = Record<string, unknown>;

interface Props {
  row: DataRecord;
  handleDeleteRowClick: (row: DataRecord) => void;
  handleEditRowClick: (row: DataRecord) => void;
  columns?: DataRecord[];
  handle24HoursClick?: (row: DataRecord) => void;
  updatedRowId?: string | null;
}

const MainHoursConfigTableRow = ({
  row: rowData,
  handleDeleteRowClick,
  handleEditRowClick,
}: Props) => {
  const row = rowData as HoursConfigRow;
  const theme = useTheme();
  // Use row values directly - no local state needed to avoid sync issues
  const start = row.start;
  const end = row.end;
  const day = row.day;
  const checked = row.isOpenFor24Hours ?? false;
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const toggleDeleteDialogOpen = () => {
    setDeleteDialogOpen(!deleteDialogOpen);
  };

  const handleDeleteCurrentRow = () => {
    handleDeleteRowClick(row);
  };

  const buildCurrentRow = (): HoursConfigRow => {
    return {
      ...row,
      start,
      end,
      day,
      isOpenFor24Hours: checked,
    };
  };

  const handleDayChange = (selectedDay: DayOption) => {
    if (selectedDay.value !== day) {
      const newRow = buildCurrentRow();
      newRow.day = selectedDay.value;
      handleEditRowClick(newRow);
    }
  };

  const handleOpen24Hours = (value: React.ChangeEvent<HTMLInputElement>) => {
    if (checked !== value.target.checked) {
      const newRow = buildCurrentRow();
      newRow.isOpenFor24Hours = value.target.checked;
      if (value.target.checked) {
        newRow.start = "00:00";
        newRow.end = "00:00";
      } else {
        newRow.start = "09:00";
        newRow.end = "17:00";
      }
      handleEditRowClick(newRow);
    }
  };

  const handleStarTimeChange = (value: string) => {
    const formattedValue = dayjs(value, "hh:mm A").format("HH:mm");
    if (start !== formattedValue) {
      const newRow = buildCurrentRow();
      newRow.start = formattedValue;
      if (formattedValue === "00:00" && end === "00:00") {
        newRow.isOpenFor24Hours = true;
      }
      handleEditRowClick(newRow);
    }
  };

  const handleEndTimeChange = (value: string) => {
    const formattedValue = dayjs(value, "hh:mm A").format("HH:mm");
    if (end !== formattedValue) {
      const newRow = buildCurrentRow();
      newRow.end = formattedValue;
      if (formattedValue === "00:00" && start === "00:00") {
        newRow.isOpenFor24Hours = true;
      }
      handleEditRowClick(newRow);
    }
  };

  const dialogConfig = {
    title: <SbuxTranslate>{`Delete`}</SbuxTranslate>,
    hasBackdrop: false,
    asModal: true,
  };

  return (
    <>
      <SbuxStyledTableRow sx={{ "& > *": { borderBottom: "unset" } }}>
        <SbuxStyledTableCell sx={{ padding: "0 6px 0 0" }}>
          <StyledDeleteButton
            disableFocusRipple
            disableRipple
            onClick={toggleDeleteDialogOpen}
          />
        </SbuxStyledTableCell>
        <SbuxStyledTableCell sx={{ padding: "0 6px 0 0" }}>
          <SbuxAutocomplete
            options={getDays() as DayOption[]}
            type="text"
            value={day}
            handleDayChange={(newValue) => {
              if (newValue) handleDayChange(newValue as DayOption);
            }}
            handleTimeChange={null}
          />
        </SbuxStyledTableCell>
        <SbuxStyledTableCell sx={{ padding: "0 6px 0 0" }}>
          <SbuxAutocomplete
            options={getTime(null) as TimeOption[]}
            type="time"
            value={dayjs(start, "HH:mm").format("hh:mm A")}
            handleDayChange={null}
            handleTimeChange={(newValue: string) =>
              handleStarTimeChange(newValue)
            }
            compareTo="endTime"
            compareToValue={dayjs(end, "HH:mm").format("hh:mm A")}
            isOpen24Hours={checked}
          />
        </SbuxStyledTableCell>
        <SbuxStyledTableCell sx={{ padding: "0 6px 0 0" }}>
          <SbuxAutocomplete
            options={getTime(end) as TimeOption[]}
            type="time"
            value={dayjs(end, "HH:mm").format("hh:mm A")}
            handleDayChange={null}
            handleTimeChange={(newValue: string) =>
              handleEndTimeChange(newValue)
            }
            compareTo="startTime"
            compareToValue={dayjs(start, "HH:mm").format("hh:mm A")}
            isOpen24Hours={checked}
          />
        </SbuxStyledTableCell>

        <SbuxStyledTableCell sx={{ padding: "0 6px 0 0" }}>
          <Checkbox
            inputProps={{ "aria-label": "controlled" }}
            disableFocusRipple
            disableRipple
            checked={checked}
            onChange={handleOpen24Hours}
            sx={{
              "& .MuiSvgIcon-root": { fontSize: 16 },
              color: theme.palette.primary.main,
              "&.Mui-checked": {
                color: theme.palette.primary.main,
              },
            }}
          />
        </SbuxStyledTableCell>
      </SbuxStyledTableRow>
      {deleteDialogOpen && (
        <SbuxDialog2
          open={deleteDialogOpen}
          config={dialogConfig}
          handleOnClose={toggleDeleteDialogOpen}
        >
          <DialogContent dividers>
            <Typography variant="h5">
              <SbuxTranslate>{`Are you sure you want to delete?`}</SbuxTranslate>
            </Typography>
          </DialogContent>

          <DialogActions>
            <Box className="actionContainer">
              <Button
                variant="contained"
                size="medium"
                onClick={handleDeleteCurrentRow}
              >
                <SbuxTranslate>{`Okay`}</SbuxTranslate>
              </Button>
              <Button
                variant="outlined"
                size="medium"
                onClick={toggleDeleteDialogOpen}
              >
                <SbuxTranslate>{`Cancel`}</SbuxTranslate>
              </Button>
            </Box>
          </DialogActions>
        </SbuxDialog2>
      )}
    </>
  );
};

export default MainHoursConfigTableRow;
